import { useState, useEffect } from 'react';
import { toast } from 'sonner';

const MAX_RECENT_KBS = 10;

export const useConnectedKBs = (conversationId: string | null | undefined, maxConnectedKBs: number = 3) => {
  const [connectedKBs, setConnectedKBs] = useState<string[]>([]);
  
  // Reset and reload when conversationId changes
  useEffect(() => {
    // Reset to empty if no valid conversationId
    if (!conversationId) {
      setConnectedKBs([]);
      return;
    }
    
    // Load from sessionStorage for valid conversationId
    const stored = sessionStorage.getItem(`giblet_kbs_${conversationId}`);
    if (stored) {
      try {
        setConnectedKBs(JSON.parse(stored));
      } catch (error) {
        console.error('Failed to parse stored KBs:', error);
        setConnectedKBs([]);
      }
    } else {
      // No stored KBs for this conversation - start fresh
      setConnectedKBs([]);
    }
  }, [conversationId]);

  // Only save to sessionStorage if we have a valid conversationId
  useEffect(() => {
    if (conversationId) {
      sessionStorage.setItem(`giblet_kbs_${conversationId}`, JSON.stringify(connectedKBs));
    }
  }, [connectedKBs, conversationId]);

  const connectKB = (kbId: string) => {
    setConnectedKBs(prev => {
      if (prev.includes(kbId)) return prev;
      if (prev.length >= maxConnectedKBs) {
        toast.error(`Maximum ${maxConnectedKBs} KBs can be connected`);
        return prev;
      }
      
      // Update recently used (localStorage)
      updateRecentlyUsed(kbId);
      
      return [...prev, kbId];
    });
  };

  const disconnectKB = (kbId: string) => {
    setConnectedKBs(prev => prev.filter(id => id !== kbId));
  };

  const updateRecentlyUsed = (kbId: string) => {
    try {
      const recent = JSON.parse(localStorage.getItem('giblet_recent_kbs') || '[]');
      const updated = [kbId, ...recent.filter(id => id !== kbId)].slice(0, MAX_RECENT_KBS);
      localStorage.setItem('giblet_recent_kbs', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to update recently used KBs:', error);
    }
  };

  const getRecentlyUsed = (): string[] => {
    try {
      return JSON.parse(localStorage.getItem('giblet_recent_kbs') || '[]');
    } catch (error) {
      console.error('Failed to get recently used KBs:', error);
      return [];
    }
  };

  return {
    connectedKBs,
    connectKB,
    disconnectKB,
    canConnectMore: connectedKBs.length < maxConnectedKBs,
    maxConnectedKBs,
    getRecentlyUsed
  };
};
